import { Component, type ErrorInfo, type ReactNode } from "react";
import { useRouteError } from "react-router-dom";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";

// ── Shared error UI ────────────────────────────────────────────────────────────

function GuardianUI({ error, onReset }: { error: Error; onReset?: () => void }) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
      <AlertTriangle className="mb-6 h-16 w-16 text-destructive/60" />
      <span className="mb-4 inline-block rounded-full bg-destructive/10 px-3 py-1 text-sm font-medium text-destructive">
        Something went wrong
      </span>
      <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
        This page ran into a problem
      </h1>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        {error.message || "An unexpected error occurred. Try refreshing the page."}
      </p>
      <div className="mt-6 flex gap-2">
        {onReset ? (
          <Button onClick={onReset}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Try again
          </Button>
        ) : (
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Reload
          </Button>
        )}
        <Button variant="outline" asChild>
          <a href="/">
            <Home className="mr-2 h-4 w-4" />
            Dashboard
          </a>
        </Button>
      </div>
      {import.meta.env.DEV && (
        <pre className="mt-6 max-w-lg overflow-auto rounded-md bg-muted p-4 text-left text-xs text-muted-foreground">
          {error.stack}
        </pre>
      )}
    </div>
  );
}

// ── Class-based boundary (wraps subtrees) ─────────────────────────────────────

interface GuardianProps {
  children: ReactNode;
}

interface GuardianState {
  error: Error | null;
}

export class Guardian extends Component<GuardianProps, GuardianState> {
  state: GuardianState = { error: null };

  static getDerivedStateFromError(error: Error): GuardianState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[Guardian]", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <GuardianUI
          error={this.state.error}
          onReset={() => this.setState({ error: null })}
        />
      );
    }
    return this.props.children;
  }
}

// ── Route errorElement (reads React Router's error context) ───────────────────

export function GuardianRoute() {
  const routeError = useRouteError();
  const error =
    routeError instanceof Error
      ? routeError
      : new Error(
          typeof routeError === "string"
            ? routeError
            : "An unexpected routing error occurred.",
        );
  return <GuardianUI error={error} />;
}
